<template>
  <form class="vehicle-research v-stack gap-10" @submit.prevent="onSubmit" novalidate>
    <div class="vehicle-research__content v-stack">

      <div v-if="isProductCompatibilityMode" class="vehicle-research__compatibility">
        <div class="vehicle-research__notice h-stack gap-2 align-start" :class="noticeClass">
          <span class="vehicle-research__type-icon" aria-hidden v-html="noticeIcon"></span>

          <div class="v-stack gap-2">
            <p class="vehicle-research__notice-title h8">
              {{ noticeTitle }}
            </p>
            <p class="vehicle-research__notice-text h8">{{ noticeLabel }}</p>
          </div>
        </div>
      </div>

      <div class="v-stack gap-6">
        <div v-if="isProductCompatibilityMode" class="vehicle-research__compatibility__desription-container">
          <p class="vehicle-research__description">
            {{ descriptionText }}
          </p>
        </div>

        <div class="vehicle-research__type-toggle" role="radiogroup" :aria-label="strings.type_group_label">
          <label class="vehicle-research__type-option" :class="{ 'is-selected': vehicleType === 'moto' }">
            <input
              class="sr-only"
              type="radio"
              name="vehicle-research-type"
              value="moto"
              v-model="vehicleType"
            />
            <span class="vehicle-research__type-icon" aria-hidden v-html="icons.moto"></span>
            <span class="vehicle-research__type-label h7 uppercase">{{ strings.type_moto }}</span>
          </label>

          <label v-if="showBikeVehicleType" class="vehicle-research__type-option" :class="{ 'is-selected': vehicleType === 'bici' }">
            <input
              class="sr-only"
              type="radio"
              name="vehicle-research-type"
              value="bici"
              v-model="vehicleType"
            />
            <span class="vehicle-research__type-icon" aria-hidden v-html="icons.bici"></span>
            <span class="vehicle-research__type-label h7 uppercase">{{ strings.type_bici }}</span>
          </label>
        </div>

        <div v-if="!isProductCompatibilityMode" class="v-stack gap-2">
          <p class="h7 uppercase vehicle-research__subtitle">{{ typeSubtitle }}</p>
          <p class="vehicle-research__description">{{ typeDescription }}</p> 
        </div>

        <div class="v-stack gap-3">
          <VehicleSelect
            :key="`vehicle-brand-${vehicleType}`"
            id="vehicle-research-brand"
            :label="strings.placeholder_brand"
            v-model="brand"
            :options="brands"
            :placeholder="strings.placeholder_brand"
            :disabled="isLoadingBrands"
            @click="brands.length === 0 || !brandRefreshed ? handleGetBrands() : null"
            @update:modelValue="onBrandChange"
          />

          <VehicleSelect
            :key="`vehicle-model-${brand}-${vehicleType}`"
            id="vehicle-research-model"
            :label="strings.placeholder_model"
            v-model="model"
            :options="models"
            :placeholder="strings.placeholder_model"
            :disabled="!brand || isLoadingModels"
            @click="brand && (models.length === 0 || !modelRefreshed) ? handleGetModels(brand) : null"
            @update:modelValue="onModelChange"
          />

          <VehicleSelect
            :key="`vehicle-year-${model}-${brand}-${vehicleType}`"
            id="vehicle-research-year"
            :label="strings.placeholder_year"
            v-model="year"
            :options="years"
            :placeholder="strings.placeholder_year"
            :disabled="!model || isLoadingYears"
            @click="model && (years.length === 0 || !yearRefreshed) ? handleGetYears(model) : null"
          />

          <button
            v-if="savedVehicle && (brand != savedVehicle.brand || model != savedVehicle.model || year != savedVehicle.year)"
            type="button"
            @click="clearVehicle"
            class="reset-button"
          >
            {{ strings.reset_vehicle }}
          </button>
        </div>

        <div v-if="errorMessage" class="vehicle-research__notice vehicle-research__notice--danger h-stack gap-2 align-center">
          <span class="vehicle-research__type-icon" aria-hidden v-html="this.icons.danger"></span>
          <p>{{ errorMessage }}</p>
        </div>

        <div class="v-stack gap-3">
          <button  type="submit" class="button button--primary button--skew" :disabled="!canSubmit">
            {{ savedVehicle ? strings.edit_vehicle : strings.submit }}
          </button>

          <button  v-if="savedVehicle" type="button" @click.prevent="clearSavedVehicle" class="button button--secondary remove-button" :disabled="!savedVehicle">
            {{ strings.remove_vehicle }}
          </button>
        </div>
      </div>

    </div>

    <div v-if="isProductCompatibilityMode" class="vehicle-research__compatible-list">
      <p class="h8 uppercase vehicle-research__compatible-list-title">
        {{ strings.compatible_vehicle_list }}
      </p>

      <ul v-if="compatibleVehicles.length > 0" class="vehicle-research__compatible-list-items v-stack gap-3">
        <li v-for="vehicle in compatibleVehicles" :key="compatibleVehicleKey(vehicle)">
          {{ formatVehicle(vehicle) }}
        </li>
      </ul>

      <p v-if="compatibleVehicles.length == 0">{{ strings.no_compatible_vehicles_found }}</p>
    </div>
    
  </form>
</template>

<script>
import VehicleSelect from './VehicleSelect.vue';
import {
  getBrands,
  getModels,
  getYears,
  getCompatibleItems,
  readSavedVehicle,
  getCompatibleVehiclesForProduct,
  isVehicleCompatible,
  saveVehicle,
  clearSavedVehicle as clearSavedVehicleStorage,
  formatVehicle as formatVehicleLabel,
} from '../../../js/vehicleSearch/api';

const DEFAULT_I18N = {
  drawer_title: 'Add Vehicle',
  type_group_label: 'Vehicle type',
  subtitle: 'Find accessories for your vehicle',
  description: 'Choose brand and model to see dedicated products.',
  type_moto: 'Moto',
  type_bici: 'Bike',
  placeholder_brand: 'Select brand',
  placeholder_model: 'Select model',
  placeholder_year: 'Select year',
  submit: 'Add vehicle',
  edit_vehicle: 'Edit vehicle',
  compatibility_title: 'Vehicle compatibility',
  compatible_success: 'Your vehicle is compatible.',
  compatible_danger: 'Not compatible with your vehicle',
  compatible_warning: 'No vehicles saved.',
  no_saved_vehicle: 'You have not added a favorite vehicle.',
  compatible_vehicle_list: 'List of compatible vehicles',
  another_vehicle: "Select another vehicle brand and model to check if it is compatible with this item.",
  choose_vehicle: 'Select your vehicle brand and model to check if it is compatible with this item.',
  error_api: 'Service not avaiable',
  no_compatible_vehicles_found: 'There are no compatibles vehicles',
  remove_vehicle: 'Remove vehicle',
  reset_vehicle: 'Reset vehicle'
};

function readI18n() {
  const script = document.getElementById('vehicle-research-i18n');

  if (!script?.textContent?.trim()) {
    return DEFAULT_I18N;
  }

  try {
    return { ...DEFAULT_I18N, ...JSON.parse(script.textContent) };
  } catch {
    return DEFAULT_I18N;
  }
}

function readIcons() {
  const templateTypes = document.getElementById('vehicle-research-type-icons');
  const templateCompatibility = document.getElementById('vehicle-research-compatibility-icons');

  const moto = templateTypes.content.querySelector('[data-vehicle-icon="moto"]')?.innerHTML ?? '';
  const bici = templateTypes.content.querySelector('[data-vehicle-icon="bici"]')?.innerHTML ?? '';

  const success = templateCompatibility.content.querySelector('[data-vehicle-icon="success"]')?.innerHTML ?? '';
  const warning = templateCompatibility.content.querySelector('[data-vehicle-icon="warning"]')?.innerHTML ?? '';
  const danger = templateCompatibility.content.querySelector('[data-vehicle-icon="danger"]')?.innerHTML ?? '';

  return { moto, bici, success, warning, danger };
}

export default {
  name: 'VehicleResearch',
  components: {
    VehicleSelect,
  },
  data() {
    return {
      strings: readI18n(),
      icons: readIcons(),
      vehicleType: 'moto',
      brand: readSavedVehicle()?.brand || '',
      model: readSavedVehicle()?.model || '',
      year: readSavedVehicle()?.year || '',
      brands: readSavedVehicle()?.brand ? [{id: readSavedVehicle().brand, label: readSavedVehicle().brand}] : [],
      models: readSavedVehicle()?.model ? [{id: readSavedVehicle().model, label: readSavedVehicle().model}] : [],
      years: readSavedVehicle()?.year ? [{id: readSavedVehicle().year, label: readSavedVehicle().year}] : [],
      mode: 'vehicle',
      savedVehicle: readSavedVehicle(),
      compatibleVehicles: [],
      compatibleItems: [],
      isProductCompatible: false,
      isLoadingBrands: false,
      isLoadingModels: false,
      isLoadingYears: false,
      brandRefreshed: false,
      modelRefreshed: false,
      yearRefreshed: false,
      isSubmitting: false,
      errorMessage: '',
      sku: '',
      /** Invalidates in-flight getModels / getYears so stale responses cannot overwrite state. */
      _modelsReqId: 0,
      _yearsReqId: 0,
      showBikeVehicleType: window.Grafite?.ResearchVehicle?.showBikeVehicleType ?? false,
    };
  },
  async mounted() {    
    this._listeners = [];

    document.querySelectorAll('.vehicle-compatibility [aria-controls="choose-vehicle"]').forEach((button) => {
      const handler = () => this.openCompatibilityMode();
      button.addEventListener('click', handler);
      this._listeners.push({ button, handler });
    });

    document.querySelectorAll('.header__choose-vehicle[aria-controls="choose-vehicle"]').forEach((button) => {
      const handler = () => this.openVehicleMode();
      button.addEventListener('click', handler);
      this._listeners.push({ button, handler });
    });

    const handler = async (event) => {
      this.sku = event.detail.variant.sku;
      if(window.Grafite?.ResearchVehicle?.genericProduct === 'Generic' || window.Grafite?.ResearchVehicle?.genericProduct === 'GenericMotorbike') {
        this.compatibleVehicles = [{ brand: 'all_vehicles_compatible' }];
      } else {
        this.compatibleVehicles = await getCompatibleVehiclesForProduct(this.sku);
      }
      this.updateChange();
    }

    document.addEventListener('variant:change', handler);
    document.addEventListener('dialog:force-close', () => {
      if(this.savedVehicle) {
        this.clearVehicle();
      }
    });
    document.addEventListener('overlay:force-close', () => {
      if(this.savedVehicle) {
        this.clearVehicle();
      }
    });

    this._listeners.push({ document, handler });

    this.sku = this.getCurrentProductSku();

    if (this.sku) {
      if(window.Grafite?.ResearchVehicle?.genericProduct !== '') {
        this.compatibleVehicles = [{ brand: window.Grafite?.ResearchVehicle?.genericProduct.toLowerCase() }];
      } else {
        this.compatibleVehicles = await getCompatibleVehiclesForProduct(this.sku);
      }
    }

    this.updateChange();
  },
  beforeUnmount() {
    this._listeners.forEach(({ button, handler }) => {
      button.removeEventListener('click', handler);
    });
  },
  computed: {
    canSubmit() {
      return Boolean(this.brand && this.model && this.year && (this.brand != this.savedVehicle?.brand || this.model != this.savedVehicle?.model || this.year != this.savedVehicle?.year) && !this.isSubmitting);
    },
    typeSubtitle() {
      return this.vehicleType === 'bici'
        ? (this.strings.subtitle_bici ?? this.strings.subtitle)
        : (this.strings.subtitle_moto ?? this.strings.subtitle);
    },
    typeDescription() {
      return this.vehicleType === 'bici'
        ? (this.strings.description_bici ?? this.strings.description)
        : (this.strings.description_moto ?? this.strings.description);
    },
    isProductCompatibilityMode() {
      return this.mode === 'compatibility';
    },
    savedVehicleLabel() {
      return this.savedVehicle ? formatVehicleLabel(this.savedVehicle) : '';
    },
    noticeClass() {
      if (!this.savedVehicle) {
        return 'vehicle-research__notice--warning';
      }

      return isVehicleCompatible(this.savedVehicle, this.compatibleVehicles)
      ? 'vehicle-research__notice--success'
      : 'vehicle-research__notice--danger';
    },
    noticeTitle () {
       if (!this.savedVehicle) {
          return this.strings.compatible_warning;
        }

        return isVehicleCompatible(this.savedVehicle, this.compatibleVehicles)
          ? this.strings.compatible_success
          : this.strings.compatible_danger;
    },
    noticeLabel() {
      if (!this.savedVehicle) {
        return this.strings.no_saved_vehicle;
      }

      return this.savedVehicleLabel;
    },
    descriptionText() {
      return this.savedVehicle
        ? this.strings.another_vehicle
        : this.strings.choose_vehicle;
    },
    noticeIcon() {
      if (!this.savedVehicle) return this.icons.warning;

      return isVehicleCompatible(this.savedVehicle, this.compatibleVehicles)
        ? this.icons.success
        : this.icons.danger;
    },
  },
  watch: {
    vehicleType: {
      async handler() {
        this.handleGetBrands();
      },
    },
  },
  methods: {
    async onSubmit() {
      if (!this.canSubmit) return;

      this.isSubmitting = true;
      this.errorMessage = '';

      const selectedVehicle = {
        type: this.vehicleType,
        brand: this.brand,
        model: this.model,
        year: this.year,
      };

      try {
        const items = await getCompatibleItems(
          this.brand,
          this.model,
          this.year,
        );

        this.compatibleItems = items;

        saveVehicle(selectedVehicle);

        if (!this.isProductCompatibilityMode) {
          document.getElementById('choose-vehicle')?.hide?.();
          setTimeout(() => {
            this.savedVehicle = selectedVehicle;
            this.updateChange();
          }, 200);
        } else {
          this.savedVehicle = selectedVehicle;
          this.updateChange();
        }
      } catch (error) {
        console.error(error);
        this.errorMessage = this.strings.error_api;
      } finally {
        this.isSubmitting = false;
      }
    },

    async openCompatibilityMode() {
      this.mode = 'compatibility';
      this.savedVehicle = readSavedVehicle();

      const title = document.querySelector('#choose-vehicle .choose-vehicle__title');
      if (title) title.textContent = this.strings.compatibility_title;
    },

    openVehicleMode() {
      this.mode = 'vehicle';
      this.savedVehicle = readSavedVehicle();

      const title = document.querySelector('#choose-vehicle .choose-vehicle__title');

      if (title) {
        title.textContent = this.strings.drawer_title;
      }
    },

    compatibleVehicleKey(vehicle) {
      return `${vehicle.type}-${vehicle.brand}-${vehicle.model}-${vehicle.year}`;
    },

    getCurrentProductSku() {
      const skuScript = document.querySelector('[data-product-sku]')?.getAttribute('data-product-sku');

      if (!skuScript?.trim()) {
        return '';
      }

      try {
        return skuScript?.trim();
      } catch (error) {
        console.error('Invalid product SKU JSON:', error);
        return '';
      }
    },

    syncDrawerSelectedVehicle() {
      const selectedVehicleEl = document.querySelector(
        '#choose-vehicle [data-vehicle-research-selected]',
      );

      if (!selectedVehicleEl) return;

      const shouldShowSelectedVehicle = this.mode === 'vehicle' && this.savedVehicle;

      if (!shouldShowSelectedVehicle) {
        selectedVehicleEl.textContent = '';
        selectedVehicleEl.hidden = true;
        return;
      }

      selectedVehicleEl.textContent = this.savedVehicleLabel;
      selectedVehicleEl.hidden = false;
    },

    clearSavedVehicle() {
      clearSavedVehicleStorage();

      this.savedVehicle = null;
      this.brand = '';
      this.model = '';
      this.year = '';
      this.models = [];
      this.years = [];
      this.compatibleItems = [];
      this.errorMessage = '';

      if (!this.isProductCompatibilityMode) {
        document.getElementById('choose-vehicle')?.hide?.();
      }

      this.updateChange({ reset: true });
    },

    async clearVehicle() {
      this.brand = this.savedVehicle?.brand;
      await this.handleGetModels(this.savedVehicle?.brand);
      this.model = this.savedVehicle?.model;
      await this.handleGetYears(this.savedVehicle?.model);
      this.year = this.savedVehicle?.year;
    },

    onBrandChange(value) {
      this.handleGetModels(value);
    },

    onModelChange(value) {
      this.handleGetYears(value);
    },

    getCurrentUrl() {
      const queryString  = window.location.search;
      const urlParams = new URLSearchParams(queryString);

      return {
        href: window.location.href,
        searchParams: urlParams,
      }
    },

    async handleGetBrands(){
      try {
        this.brands = this.sortOptionsAlphabetically(
          await getBrands(this.vehicleType)
        );

        this._modelsReqId += 1;
        this._yearsReqId += 1;
        this.isLoadingModels = false;
        this.isLoadingYears = false;
        this.model = '';
        this.year = '';
        this.models = [];
        this.years = [];
        this.compatibleItems = [];
        this.errorMessage = '';
        this.isLoadingBrands = true;
        this.brandRefreshed = true;
        this.modelRefreshed = false;
        this.yearRefreshed = false;
      } catch (error) {
        console.error(error);
        this.brands = [];
        this.errorMessage = this.strings.error_api;
      } finally {
        this.isLoadingBrands = false;
      }
    },

    async handleGetModels(value){
      if (!value) {
        this._modelsReqId += 1;
        this._yearsReqId += 1;
        this.isLoadingModels = false;
        this.isLoadingYears = false;
        this.model = '';
        this.year = '';
        this.years = [];
        this.compatibleItems = [];
        this.errorMessage = '';
        this.models = [];
        return;
      }

      const brandRequested = value;
      const reqId = (this._modelsReqId += 1);
      this._yearsReqId += 1;
      this.isLoadingYears = false;

      // Reset dependent fields so selects match options (stale ids show as blank).
      this.model = '';
      this.year = '';
      this.years = [];

      try {
        if (reqId !== this._modelsReqId || this.brand !== brandRequested) {
          return;
        }
        this.models = this.sortOptionsAlphabetically(
          await getModels(brandRequested)
        );
        this.compatibleItems = [];
        this.errorMessage = '';
        this.modelRefreshed = true;
        this.yearRefreshed = false;
      } catch (error) {
        console.error(error);
        if (reqId === this._modelsReqId) {
          this.models = [];
          this.errorMessage = this.strings.error_api;
        }
      }
    },

    async handleGetYears(value){
      if (!value || !this.brand) {
        this._yearsReqId += 1;
        this.years = [];
        this.isLoadingYears = false;
        return;
      }

      const brandSnapshot = this.brand;
      const modelSnapshot = value;
      const reqId = (this._yearsReqId += 1);

      this.year = '';
      this.compatibleItems = [];
      this.errorMessage = '';

      try {
        if (reqId !== this._yearsReqId || this.brand !== brandSnapshot || this.model !== modelSnapshot) {
          return;
        }
        this.years = this.sortYearsNewestFirst(
          await getYears(brandSnapshot, modelSnapshot)
        );
        this.yearRefreshed = true;
      } catch (error) {
        console.error(error);
        if (reqId === this._yearsReqId) {
          this.years = [];
          this.errorMessage = this.strings.error_api;
        }
      }
    },
    formatVehicle(vehicle) {
      return formatVehicleLabel(vehicle);
    },
    sortOptionsAlphabetically(options) {
      return [...options].sort((a, b) => {
        const labelA = String(a.label ?? a.id ?? '');
        const labelB = String(b.label ?? b.id ?? '');

        return labelA.localeCompare(labelB, 'it', {
          sensitivity: 'base',
          numeric: true,
        });
      });
    },

    sortYearsNewestFirst(options) {
      return [...options].sort((a, b) => {
        const yearA = Number(a.label ?? a.id);
        const yearB = Number(b.label ?? b.id);

        return yearB - yearA;
      });
    },

    updateChange (reset = false){
      if (!this.savedVehicle && !reset) return;

      if(!reset){
        this.isProductCompatible = isVehicleCompatible(this.savedVehicle, this.compatibleVehicles);
      }

      document.dispatchEvent(
        new CustomEvent('vehicle-research:selected', {
          detail: {
            vehicle: this.savedVehicle,
            vehicleLabel: formatVehicleLabel(this.savedVehicle),
            url: this.getCurrentUrl(),
            compatible: reset ? null : this.isProductCompatible,
          },
        }),
      );
    }
  },
  
};
</script>
